import { FormErrorsAlert } from '@framework/form/form/errors/FormErrorsAlert.tsx';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { FormSection } from '@framework/form/form/section/FormSection.tsx';
import { type ColumnType } from 'antd/es/table';
import type * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { SETTING_COLUMN_KEY } from '../addColumnPicker.tsx';
import { type IColumnPickerProps } from '../ColumnPicker.tsx';
import { ColumnPickerLocalState } from '../ColumnPickerLocalState.ts';
import {
  type ColumnPickerConfig,
  ColumnPickerSchema,
} from './ColumnPickerSchema.tsx';

export interface IColumnPickerFormFormProps<T> extends IColumnPickerProps<T> {
  isFetching?: boolean;
  total?: number;
  children?: React.ReactNode;
  onSave?: (config: ColumnPickerConfig) => void;
}

/**
 * Trasforma le colonne in un formato compatibile con il `ColumnPicker`.
 */
function mapColumnsToOptions<T>(columns: Array<ColumnType<T>>) {
  return columns.map((f, i) => {
    return {
      id: v4(),
      index: i,
      key: f.key as string,
      visible: f.hidden ? false : true,
      fixed: f.fixed == 'left' ? true : f.fixed == 'right' ? true : false,
      showInExpandedRow: f.showInExpandedRow,
      // fixable: previousRows?.fixed == 'left' ? true : false,
      title: f.title?.toString() ?? '-',
    };
  });
}

/**
 * Date le colonne disponibili e l'id del `ColumnPicker`, restituisce le colonne
 * selezionabili.
 */
function getInitialValues<T>(
  id: string | null | undefined,
  columns: Array<ColumnType<T>>,
) {
  if (!id) return { columns: [] };

  // leggiamo la configurazione salvata
  const savedConfig = ColumnPickerLocalState.read(id);

  return {
    columns: [
      // aggiungiamo le colonne salvate
      ...(savedConfig?.columns ?? []),
      // e le altre disponibili che non sono ancora salvate
      ...mapColumnsToOptions(
        columns.filter(
          c => !savedConfig?.columns?.some(saved => saved.key == c.key),
        ),
      ),
    ],
  };
}

/**
 * Versione del form "AdvancedSearch" che si espande e si contrae, con
 * messaggio di filtro attivo.
 * Da mettere come `title` delle `TableData`.
 */
export function ColumnPickerForm<T>(props: IColumnPickerFormFormProps<T>) {
  const { columns, id, children, onSave } = props;

  // Escludo la colonna delle impostazioni dalle colonne disponibili
  const pickableColumns = useMemo(() => {
    return columns?.filter(c => c.key != SETTING_COLUMN_KEY) ?? [];
  }, [columns]);

  const { initialColumns, defaultColumns } = useMemo(() => {
    return {
      initialColumns: getInitialValues(id, pickableColumns),
      defaultColumns: mapColumnsToOptions(pickableColumns),
    };
  }, [id, pickableColumns]);

  const [initialValues, setInitialValues] =
    useState<ColumnPickerConfig>(initialColumns);

  const handleChange = useCallback(
    (values: ColumnPickerConfig) => {
      if (!id) {
        return;
      }

      ColumnPickerLocalState.write(id, values);

      onSave?.(values);
      setInitialValues(values);
    },
    [id, onSave],
  );

  return (
    <div>
      <FormHandler
        schema={ColumnPickerSchema}
        initialValues={initialValues}
        onReset={() => {
          handleChange({ columns: defaultColumns });
        }}
        // hideVisualFeedback
        onSubmit={async (rawValues, event, methods) => {
          try {
            const values = await ColumnPickerSchema.validate(rawValues);
            handleChange(values);
          } catch (e) {
            console.error(`[Errore] Errore nella ricerca`, e);
          }
        }}
      >
        <FormErrorsAlert />
        <FormSection removeHelpMargin>{children}</FormSection>
      </FormHandler>
    </div>
  );
}
